import React from "react";
import {
  MapPin,
  Mail,
  Dumbbell,
  ArrowUpRight,
  Briefcase,
  GraduationCap
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import SectionHeader from "./SectionHeader";
import { useLanguage } from "../language/LanguageContext";
import Tag from "./ui/Tag";
import SocialLinks from "./ui/SocialLinks";
import Reveal from "./ui/Reveal";
import { REMI_URL } from "../constants";

const EXPERTISE = [
  "Javascript",
  "Node.js",
  "Vue.js",
  "React.js",
  "React native",
  "PostgreSQL",
  "MySQL",
  "SQL Server",
  "AWS services",
  "Azure DevOps",
  "Nest.js",
  "MongoDB"
];

const AboutSection = () => {
  const { translations } = useLanguage();
  const { about, experience, education } = translations;

  return (
    <section>
      <SectionHeader title={about.title} />

      <div className="flex flex-col lg:flex-row gap-10">
        <div className="lg:w-1/3 lg:flex-shrink-0">
          <Reveal className="lg:sticky lg:top-24">
            <div className="card p-5">
              <h3 className="text-xs font-medium uppercase tracking-[0.18em] text-accent mb-5">
                {about.facts.title}
              </h3>

              <dl className="space-y-4">
                <Fact
                  icon={Briefcase}
                  label={about.facts.currently}
                  value={experience.rise.role}
                  detail={experience.rise.company}
                />
                <Fact
                  icon={Dumbbell}
                  label={about.facts.founder}
                  value={about.remi.name}
                  detail={about.remi.tagline}
                  href={REMI_URL}
                  external
                />
                <Fact
                  icon={GraduationCap}
                  label={about.facts.education}
                  value={education.degree}
                  detail={education.university}
                />
                <Fact
                  icon={MapPin}
                  label={about.facts.basedIn}
                  value={about.location}
                />
                <Fact
                  icon={Mail}
                  label={about.facts.email}
                  value={about.email}
                  href={`mailto:${about.email}`}
                />
              </dl>

              <div className="pt-5 mt-5 border-t border-dark-border">
                <SocialLinks size={18} variant="boxed" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:w-2/3 min-w-0">
          <Reveal delay={80}>
            <p className="text-lg mb-8 text-dark-text leading-relaxed max-w-2xl">{about.description}</p>
          </Reveal>

          {/* Founder highlight — the standout card in this section */}
          <Reveal delay={140}>
            <div className="card-interactive overflow-hidden mb-8 p-5">
              <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent"
              />

              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/25 text-accent flex-shrink-0">
                  <Dumbbell size={20} />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-dark-heading">{about.remi.name}</h3>
                    <span className="px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-accent bg-accent/10 border border-accent/30 rounded-full">
                      {about.remi.badge}
                    </span>
                  </div>
                  <p className="text-sm text-dark-muted">{about.remi.tagline}</p>
                </div>
              </div>

              <p className="text-dark-text leading-relaxed mb-4">{about.remi.description}</p>

              <div className="flex flex-wrap gap-2">
                {about.remi.stack.map((tech: string) => (
                  <Tag key={tech} onSurface>
                    {tech}
                  </Tag>
                ))}
              </div>

              <a
                href={REMI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-accent bg-accent/10 border border-accent/30 transition duration-300 ease-out-soft hover:bg-accent/15 hover:border-accent/50 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface"
              >
                {about.remi.cta}
                <ArrowUpRight
                  size={15}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <h3 className="text-xl font-semibold mb-4 text-dark-heading">
              {about.expertiseTitle}
            </h3>
            <div className="flex flex-wrap gap-2">
              {EXPERTISE.map((skill) => (
                <Tag key={skill}>{skill}</Tag>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/** One labelled row in the at-a-glance card. */
const Fact = ({
  icon: Icon,
  label,
  value,
  detail,
  href,
  external = false
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  detail?: string;
  href?: string;
  external?: boolean;
}) => {
  const body = (
    <>
      <span className="block text-sm text-dark-heading font-medium leading-snug break-words">
        {value}
      </span>
      {detail && (
        <span className="block text-sm text-dark-muted leading-snug break-words">{detail}</span>
      )}
    </>
  );

  return (
    <div className="flex items-start gap-3">
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 text-accent flex-shrink-0 mt-0.5">
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-wide text-dark-muted mb-0.5">{label}</dt>
        <dd className="m-0">
          {href ? (
            <>
              <a
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                className="group inline-flex items-center gap-1 text-sm text-dark-heading font-medium leading-snug break-words hover:text-accent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
              >
                {value}
                {external && (
                  <ArrowUpRight
                    size={13}
                    className="text-dark-muted transition-all duration-200 group-hover:text-accent group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                )}
              </a>
              {detail && (
                <span className="block text-sm text-dark-muted leading-snug break-words">
                  {detail}
                </span>
              )}
            </>
          ) : (
            body
          )}
        </dd>
      </div>
    </div>
  );
};

export default AboutSection;
